// The resolve popup of a write conflict (HUBX-8466; design "Listing — shared write rules",
// Block B).
//
// Pure, and shared by every surface: which members the popup lists, how each side of a member
// reads, what each choice starts on, and how the choices become the next edit. Holding the
// conflict, re-reading the item and applying the result to a store or a form are the
// surface's job.
//
// A 412 no longer rebases anything (2026-10-19): the item keeps its baseline, its tokens and
// its edit until the user resolves it here, against the item as re-read.

import { ICreateItemDiscountQty, ILookupAttribute, ILookupCategory, ILookupExwPoint, ILookupManufacturer, ItemResponse } from '../item.models';
import { CONFLICT_FIELDS, IAttributeLookups, attributeValue, comparable } from './item-write-concurrency';
import { SKU_STATUS_OPTIONS } from './item-write-status.rules';

export type ResolveChoice = 'yours' | 'current';

/** What the popup needs to show a member the way the surface shows it: names, not ids. */
export interface IResolveLookups extends IAttributeLookups {
  manufacturers: ILookupManufacturer[];
  categories: ILookupCategory[];
  exwPoints: ILookupExwPoint[];
}

/** One side of the comparison. `source` is an ItemResponse or a request - the round-trip
 *  invariant gives both the same member names - and `status` is passed apart because the
 *  response calls it `effectiveStatus` and the modal's form holds it as `skuStatus`. */
export interface IResolveSide {
  source: { [key: string]: any };
  status: string;
}

/** One member the popup lists: on screen and re-read, it differs. */
export interface IResolveRow {
  key: string;
  label: string;
  /** The value on screen, as the surface shows it. */
  yours: string;
  /** The value re-read, as the surface shows it. */
  current: string;
  /** The edit moved this member away from the baseline - the user typed it. */
  edited: boolean;
  /** This caller may write it now (the re-read item's writableFields). When not, the row
   *  offers Current only. */
  writable: boolean;
}

/** What an empty member reads as. */
export const EMPTY_VALUE = '—';

/** The side of an ItemResponse: its own projection is its status. */
export function itemSide(item: ItemResponse): IResolveSide {
  return { source: item, status: item.effectiveStatus };
}

/**
 * The rows of the popup: every member whose value on screen differs from the value re-read,
 * whether or not the user edited it.
 *
 * `tags` is never listed: no surface edits it, so the only reading of it is the stored one.
 * Neither is a `todaysDeals` the body leaves out: absent means "do not touch", and there is
 * nothing to choose.
 */
export function resolveRows(
  baseline: IResolveSide,
  current: IResolveSide,
  mine: IResolveSide,
  writableFields: string[],
  lookups: IResolveLookups
): IResolveRow[] {
  const before = comparable(baseline.source, baseline.status, lookups);
  const after = comparable(current.source, current.status, lookups);
  const ours = comparable(mine.source, mine.status, lookups);

  const rows: IResolveRow[] = [];
  for (const field of CONFLICT_FIELDS) {
    if (!field.writable) { continue; }
    const yours = ours.get(field.key);
    if (yours === undefined || yours === after.get(field.key)) { continue; }
    rows.push({
      key: field.key,
      label: field.label,
      yours: displayValue(field.key, mine, lookups),
      current: displayValue(field.key, current, lookups),
      edited: yours !== before.get(field.key),
      writable: writableFields.includes(field.writable)
    });
  }
  return rows;
}

/** What a row starts on: what the user typed stays theirs, everything else is the item as it
 *  is now. A member this caller may no longer write is Current, and stays so. */
export function initialChoice(row: IResolveRow): ResolveChoice {
  return row.writable && row.edited ? 'yours' : 'current';
}

/** Every row Current - Discard my changes, the popup's one bulk action. There is no "every
 *  row Yours": it would put back the stale value of every member the user never touched. */
export function allCurrent(rows: readonly IResolveRow[]): Map<string, ResolveChoice> {
  return new Map(rows.map(row => [row.key, 'current'] as [string, ResolveChoice]));
}

/**
 * The next edit of a listing row: the item as re-read, with the members chosen as Yours taken
 * from the edit. The surface makes the re-read item the new baseline, so the result carries
 * its tokens and its writableFields. A member this caller may not write is Current whatever
 * the choice says.
 */
export function resolvedEdit(
  current: ItemResponse,
  edited: ItemResponse,
  rows: readonly IResolveRow[],
  choices: ReadonlyMap<string, ResolveChoice>,
  lookups: IAttributeLookups
): ItemResponse {
  const next = clone(current);
  for (const row of rows) {
    if (row.writable && choices.get(row.key) === 'yours') {
      copyMember(next, row.key, edited, lookups);
    }
  }
  return next;
}

const ATTRIBUTE_KEYS: { [key: string]: keyof IAttributeLookups } = {
  'attributes.Condition': 'condition',
  'attributes.Warranty': 'warranty',
  'attributes.Packaging': 'packaging'
};

/** One member of `source` into `target`. An attribute is one entry of the list, found by its
 *  lookup id; the status is the projection the edit carries its intent in; every other key
 *  is the member itself. */
export function copyMember(target: ItemResponse, key: string, source: ItemResponse, lookups: IAttributeLookups): void {
  const attribute = ATTRIBUTE_KEYS[key];
  if (attribute) {
    const id = lookups[attribute]?.id;
    if (!id) { return; }
    const chosen = (source.attributes ?? []).find(a => a.attributeId === id);
    const others = (target.attributes ?? []).filter(a => a.attributeId !== id);
    const position = (target.attributes ?? []).findIndex(a => a.attributeId === id);
    if (!chosen) {
      target.attributes = others;
    } else if (position < 0) {
      target.attributes = [...others, { ...chosen }];
    } else {
      target.attributes = (target.attributes ?? []).map(a => a.attributeId === id ? { ...chosen } : a);
    }
    return;
  }
  const value = (source as unknown as { [k: string]: unknown })[key];
  (target as unknown as { [k: string]: unknown })[key] = value !== null && typeof value === 'object' ? clone(value) : value;
}

/** A member as the surface shows it: names through /lookups, money and tiers formatted, an
 *  empty member as EMPTY_VALUE. */
export function displayValue(key: string, side: IResolveSide, lookups: IResolveLookups): string {
  const s = side.source;
  switch (key) {
    case 'manufacturerId':
      return named(lookups.manufacturers.find(m => m.id === s['manufacturerId'])?.name, s['manufacturerId']);
    case 'categoryId': {
      // The effective category: the override, else the master's (ADR-0007), as the form shows it.
      const id = s['categoryId'] ?? s['masterCategoryId'];
      const trail = id ? categoryTrail(lookups.categories, id) : null;
      return trail ? trail.join(' › ') : named(null, id);
    }
    case 'exwId':
      return named(lookups.exwPoints.find(e => String(e.id) === String(s['exwId']))?.description, s['exwId']);
    case 'effectiveStatus':
      return named(SKU_STATUS_OPTIONS.find(o => o.value === side.status)?.label, side.status);
    case 'attributes.Condition':
      return attributeText(s['attributes'], lookups.condition);
    case 'attributes.Warranty':
      return attributeText(s['attributes'], lookups.warranty);
    case 'attributes.Packaging':
      return attributeText(s['attributes'], lookups.packaging);
    case 'unitPrice':
    case 'unitPurchasePrice':
      return money(s[key]);
    case 'discountQtys':
      return tiers(s['discountQtys']);
    case 'isArchived':
    case 'todaysDeals':
      return s[key] ? 'Yes' : 'No';
    default:
      return text(s[key]);
  }
}

function categoryTrail(tree: ILookupCategory[] | undefined, id: string): string[] | null {
  for (const node of tree ?? []) {
    if (node.categoryId === id) { return [node.name]; }
    const below = categoryTrail(node.children, id);
    if (below) { return [node.name, ...below]; }
  }
  return null;
}

function attributeText(attributes: { attributeId: string; value?: string }[] | undefined, lookup?: ILookupAttribute): string {
  const code = attributeValue(attributes, lookup);
  if (code == null || code === '') { return EMPTY_VALUE; }
  return lookup?.values.find(v => v.code === code)?.value ?? String(code);
}

function named(name: string | null | undefined, raw: unknown): string {
  return name || text(raw);
}

function text(value: unknown): string {
  return value == null || value === '' ? EMPTY_VALUE : String(value);
}

function money(value: unknown): string {
  if (value == null || value === '') { return EMPTY_VALUE; }
  return '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function tiers(value: ICreateItemDiscountQty[] | null | undefined): string {
  const list = (value ?? []).filter(t => t && t.fromQty != null && t.price != null);
  return list.length ? list.map(t => `${t.fromQty}+ at ${money(t.price)}`).join(', ') : EMPTY_VALUE;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
